import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../context/CartContext';

export default function Cart() {
  const { items, removeItem, updateQuantity, totalPrice, totalItems, clearCart } = useCart();
  const [coupon, setCoupon] = useState('');
  const [discount, setDiscount] = useState(0);
  const [couponMsg, setCouponMsg] = useState('');
  const [orderStatus, setOrderStatus] = useState<'idle' | 'form' | 'success' | 'error'>('idle');
  const [orderForm, setOrderForm] = useState({ name: '', email: '', phone: '', address: '', city: '', state: '', zip: '' });

  const shipping = totalPrice > 30 ? 0 : 4.99;
  const discountAmt = totalPrice * discount;
  const finalTotal = totalPrice - discountAmt + shipping;

  const applyCoupon = () => {
    if (coupon.toUpperCase() === 'LITTLELOOP20') {
      setDiscount(0.2);
      setCouponMsg('20% discount applied!');
    } else {
      setDiscount(0);
      setCouponMsg('Invalid code.');
    }
  };

  const handleOrderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleOrderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const itemsText = items.map(i => `${i.name} x${i.quantity} ($${(i.price * i.quantity).toFixed(2)})`).join(', ');
      const body = new URLSearchParams({
        name: orderForm.name,
        email: orderForm.email,
        phone: orderForm.phone,
        address: `${orderForm.address}, ${orderForm.city}, ${orderForm.state} ${orderForm.zip}`,
        items: itemsText,
        total: `$${finalTotal.toFixed(2)}`,
        coupon: coupon,
      });
      await fetch('https://readdy.ai/api/form/d70r5cf9hgr6oot2r4rg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setOrderStatus('success');
      clearCart();
    } catch {
      setOrderStatus('error');
    }
  };

  if (orderStatus === 'success') {
    return (
      <div style={{ fontFamily: "'Nunito', sans-serif" }}>
        <Navbar />
        <section className="min-h-screen flex items-center justify-center py-28 px-6">
          <div className="text-center max-w-md">
            <div className="flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mx-auto mb-6">
              <i className="ri-check-double-line text-green-600 text-4xl"></i>
            </div>
            <h2 className="text-3xl font-700 text-gray-800 mb-3" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700 }}>
              Order Received!
            </h2>
            <p className="text-gray-500 mb-8">We have received your order. We will contact you soon to arrange payment and shipping.</p>
            <Link to="/products" className="inline-block bg-brand-yellow text-white px-8 py-3 rounded-full font-600 hover:bg-brand-yellow-dark transition-colors cursor-pointer whitespace-nowrap" style={{ fontWeight: 600 }}>
              Keep Shopping
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Nunito', sans-serif" }}>
      <Navbar />

      <section className="pt-28 pb-10" style={{ backgroundColor: '#F5F2FD' }}>
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl font-700 text-gray-800" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700 }}>
            My Cart
          </h1>
          <p className="text-gray-500 text-sm mt-2">{totalItems} item{totalItems !== 1 ? 's' : ''} in your cart</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {items.length === 0 ? (
            <div className="text-center py-20">
              <div className="flex items-center justify-center w-20 h-20 mx-auto mb-4">
                <i className="ri-shopping-basket-line text-6xl text-gray-200"></i>
              </div>
              <p className="text-gray-400 text-lg mb-6">Your cart is empty</p>
              <Link to="/products" className="inline-block bg-brand-yellow text-white px-8 py-3 rounded-full font-600 hover:bg-brand-yellow-dark transition-colors cursor-pointer whitespace-nowrap" style={{ fontWeight: 600 }}>
                Browse Products
              </Link>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1 space-y-4">
                {items.map(item => (
                  <div key={item.id} className="bg-white rounded-2xl p-5 flex items-center gap-5">
                    <div className="w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-400 mb-0.5">{item.category} · Size {item.size}</p>
                      <h3 className="font-700 text-gray-800 text-sm" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700 }}>{item.name}</h3>
                      <p className="text-brand-yellow font-700 mt-1" style={{ fontWeight: 700 }}>${item.price.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer transition-colors whitespace-nowrap">
                        <i className="ri-subtract-line text-sm"></i>
                      </button>
                      <span className="w-8 text-center text-sm font-600" style={{ fontWeight: 600 }}>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer transition-colors whitespace-nowrap">
                        <i className="ri-add-line text-sm"></i>
                      </button>
                    </div>
                    <p className="font-700 text-gray-800 w-20 text-right hidden sm:block" style={{ fontWeight: 700 }}>${(item.price * item.quantity).toFixed(2)}</p>
                    <button onClick={() => removeItem(item.id)} className="flex items-center justify-center w-9 h-9 rounded-full text-gray-400 hover:bg-red-50 hover:text-red-500 cursor-pointer transition-colors whitespace-nowrap">
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                ))}
              </div>

              <div className="lg:w-96">
                <div className="bg-white rounded-2xl p-6 sticky top-24">
                  <h2 className="font-700 text-gray-800 mb-5" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700 }}>Order Summary</h2>

                  <div className="mb-5">
                    <label className="block text-xs text-gray-500 mb-1.5 font-600" style={{ fontWeight: 600 }}>Discount code</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={coupon}
                        onChange={e => setCoupon(e.target.value)}
                        placeholder="LITTLELOOP20"
                        className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-brand-yellow"
                      />
                      <button onClick={applyCoupon} className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-600 hover:bg-gray-200 cursor-pointer whitespace-nowrap" style={{ fontWeight: 600 }}>
                        Apply
                      </button>
                    </div>
                    {couponMsg && <p className={`text-xs mt-1.5 ${discount > 0 ? 'text-green-600' : 'text-red-500'}`}>{couponMsg}</p>}
                  </div>

                  <div className="space-y-3 border-t border-gray-100 pt-4 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Subtotal</span>
                      <span className="text-gray-700">${totalPrice.toFixed(2)}</span>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-600">Discount (20%)</span>
                        <span className="text-green-600">-${discountAmt.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Shipping</span>
                      <span className="text-gray-700">{shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span>
                    </div>
                    {shipping > 0 && <p className="text-xs text-gray-400">Free shipping on orders over $30</p>}
                    <div className="flex justify-between font-700 text-lg pt-2 border-t border-gray-100" style={{ fontWeight: 700 }}>
                      <span className="text-gray-800">Total</span>
                      <span className="text-brand-yellow">${finalTotal.toFixed(2)}</span>
                    </div>
                  </div>

                  {orderStatus === 'form' ? (
                    <form onSubmit={handleOrderSubmit} data-readdy-form className="space-y-3">
                      <h3 className="font-700 text-gray-800 text-sm mb-3" style={{ fontWeight: 700, fontFamily: "'Quicksand', sans-serif" }}>Shipping Details</h3>
                      {[
                        { name: 'name', placeholder: 'Full name' },
                        { name: 'email', placeholder: 'Email' },
                        { name: 'phone', placeholder: 'Phone' },
                        { name: 'address', placeholder: 'Street address' },
                        { name: 'city', placeholder: 'City' },
                        { name: 'state', placeholder: 'State' },
                        { name: 'zip', placeholder: 'ZIP Code' },
                      ].map(field => (
                        <input
                          key={field.name}
                          type={field.name === 'email' ? 'email' : 'text'}
                          name={field.name}
                          required
                          placeholder={field.placeholder}
                          value={(orderForm as Record<string, string>)[field.name]}
                          onChange={handleOrderChange}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-brand-yellow bg-gray-50"
                        />
                      ))}
                      {orderStatus === 'error' && <p className="text-xs text-red-500">Error submitting. Please try again.</p>}
                      <button type="submit" className="w-full py-3.5 bg-brand-yellow text-white rounded-full font-600 hover:bg-brand-yellow-dark transition-colors cursor-pointer whitespace-nowrap" style={{ fontWeight: 600 }}>
                        Confirm Order
                      </button>
                      <button type="button" onClick={() => setOrderStatus('idle')} className="w-full py-2 text-gray-400 text-sm cursor-pointer hover:text-gray-600 transition-colors whitespace-nowrap">
                        Cancel
                      </button>
                    </form>
                  ) : (
                    <button onClick={() => setOrderStatus('form')} className="w-full py-3.5 bg-brand-yellow text-white rounded-full font-600 hover:bg-brand-yellow-dark transition-colors cursor-pointer whitespace-nowrap" style={{ fontWeight: 600 }}>
                      Proceed to Checkout
                    </button>
                  )}

                  <div className="flex items-center justify-center gap-3 mt-4">
                    <i className="ri-secure-payment-line text-gray-400 text-sm"></i>
                    <span className="text-xs text-gray-400">100% secure checkout</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
